import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Modal } from "./Guests";
import { Crown, Plus, Copy, Eye } from "lucide-react";

export default function UserAccounts() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", show_name: "", access_tier: "free", temp_password: "" });
  const [createdTemp, setCreatedTemp] = useState(null);
  const { impersonate } = useAuth();
  const navigate = useNavigate();

  const viewAs = async (u) => {
    await impersonate(u.id);
    navigate("/dashboard");
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const { data } = await api.post("/admin/users", form);
    setCreatedTemp({ email: form.email, password: data.temp_password });
    setForm({ name: "", email: "", show_name: "", access_tier: "free", temp_password: "" });
    load();
  };

  const toggle = async (u) => {
    if (u.role === "super_admin") return;
    await api.put(`/admin/users/${u.id}`, { suspended: !u.suspended }); load();
  };
  const setTier = async (u, t) => { await api.put(`/admin/users/${u.id}`, { access_tier: t }); load(); };

  const superAdmin = users.find(u => u.role === "super_admin");
  const admins = users.filter(u => u.role !== "super_admin");

  return (
    <div className="pgm-reveal">
      <div className="pgm-micro">◆ Super Admin</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 className="pgm-display" style={{ fontSize: 44 }}>User Accounts</h1>
        <button className="pgm-btn" onClick={() => setOpen(true)} data-testid="add-user-btn"><Plus size={14} /> Add admin</button>
      </div>

      {superAdmin && (
        <div className="pgm-card" style={{ background: "rgba(78,29,94,0.07)", borderLeftColor: "var(--pgm-amber)", marginTop: 16, marginBottom: 22 }} data-testid="superadmin-card">
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <Crown size={26} color="var(--pgm-magenta)" />
            <div>
              <div className="pgm-micro">◆ Super Admin (you)</div>
              <div className="pgm-display" style={{ fontSize: 22 }}>{superAdmin.name}</div>
              <div style={{ fontSize: 13, opacity: 0.75 }}>{superAdmin.email}</div>
            </div>
          </div>
        </div>
      )}

      <h2 className="pgm-display" style={{ fontSize: 22, marginBottom: 10 }}>Admins</h2>
      {admins.length === 0 ? (
        <div className="pgm-callout"><strong>No admin accounts yet.</strong> Click "Add admin" to create the first podcaster account.</div>
      ) : (
        <table className="pgm-table" data-testid="users-table">
          <thead><tr><th>Name</th><th>Email</th><th>Tier</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {admins.map(u => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td data-label="Name"><strong>{u.name}</strong></td>
                <td data-label="Email">{u.email}</td>
                <td data-label="Tier">
                  <select className="pgm-select" style={{ width: 110 }} value={u.access_tier} onChange={(e) => setTier(u, e.target.value)} data-testid={`user-tier-${u.id}`}>
                    <option value="free">Free</option><option value="paid">Paid</option>
                  </select>
                </td>
                <td data-label="Status">{u.suspended ? <span className="pgm-badge pgm-badge-orange">Suspended</span> : <span className="pgm-badge pgm-badge-olive">Active</span>}</td>
                <td data-label="Actions" style={{ whiteSpace: "nowrap" }}>
                  {!u.suspended && (
                    <button className="pgm-btn pgm-btn-olive" style={{ padding: "5px 12px", fontSize: 10, marginRight: 4 }} onClick={() => viewAs(u)} data-testid={`user-view-as-${u.id}`}><Eye size={11} /> View as</button>
                  )}
                  <button className="pgm-btn pgm-btn-ghost" style={{ padding: "5px 12px", fontSize: 10 }} onClick={() => toggle(u)} data-testid={`user-toggle-${u.id}`}>{u.suspended ? "Reinstate" : "Suspend"}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open && (
        <Modal onClose={() => { setOpen(false); setCreatedTemp(null); }} title="Add admin account" width={520}>
          {createdTemp ? (
            <div>
              <div className="pgm-callout" style={{ background: "var(--pgm-olive)", color: "var(--pgm-cream)", marginBottom: 14 }}>
                <strong>Account created!</strong> Share these credentials with the user — they'll be prompted to change the password on first login.
              </div>
              <div className="pgm-card">
                <div style={{ marginBottom: 8 }}><strong>Email:</strong> {createdTemp.email}</div>
                <div><strong>Temporary password:</strong> <code>{createdTemp.password}</code></div>
              </div>
              <button className="pgm-btn" onClick={() => navigator.clipboard.writeText(`Email: ${createdTemp.email}\nPassword: ${createdTemp.password}`)} style={{ marginTop: 12 }}>
                <Copy size={14} /> Copy credentials
              </button>
            </div>
          ) : (
            <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div><label className="pgm-label">Name *</label><input className="pgm-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
              <div><label className="pgm-label">Email *</label><input className="pgm-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
              <div><label className="pgm-label">Show name *</label><input className="pgm-input" required value={form.show_name} onChange={(e) => setForm({ ...form, show_name: e.target.value })} data-testid="new-user-show" /></div>
              <div><label className="pgm-label">Access tier</label>
                <select className="pgm-select" value={form.access_tier} onChange={(e) => setForm({ ...form, access_tier: e.target.value })}>
                  <option value="free">Free</option><option value="paid">Paid</option>
                </select>
              </div>
              <div><label className="pgm-label">Temporary password *</label><input className="pgm-input" required value={form.temp_password} onChange={(e) => setForm({ ...form, temp_password: e.target.value })} data-testid="new-user-password" /></div>
              <button className="pgm-btn" type="submit" data-testid="new-user-submit">Create account</button>
            </form>
          )}
        </Modal>
      )}
    </div>
  );
}
